/**
 * What a customer is allowed to attach to a named event.
 *
 * The rules here are stricter than storage needs and exactly as strict as the
 * promise needs. A named event is the one part of Panel a customer fills in
 * themselves, so it is the one place data we never asked for can arrive:
 * `panel('checkout', { email: user.email })` is written in good faith by
 * somebody who has not thought about it, and it would put a direct identifier
 * in a row next to a visitor id.
 *
 * ## This is a clamp, not the clamp
 *
 * `sanitizeEventProps` in `@panel/shared/analytics` is the one that decides,
 * because the server is the trust boundary: the ingest path takes no credential
 * beyond a key that sits in public page source, and a copy of this script built
 * last year keeps running on a site we do not control until its owner
 * redeploys. This copy exists so that the data does not LEAVE the page in the
 * first place, which is the only protection that works when the network between
 * here and there is somebody else's.
 *
 * The two therefore read the same constants rather than agreeing by hand. Where
 * they differ it is in one direction only, and deliberately: the browser copy
 * redacts e-mail addresses out of values, which the server's URL-based
 * redaction does not catch in a bare `user@example.com`.
 */
import {
  MAX_EVENT_NAME_LENGTH,
  MAX_EVENT_PROPS,
  MAX_PROP_KEY_LENGTH,
  MAX_PROP_VALUE_LENGTH,
  RESERVED_EVENT_NAME_PREFIX,
  type EventPropValue,
} from '@panel/shared/analytics';

/**
 * `a-z0-9_.-`, starting with a letter or a digit.
 *
 * The same rule `sanitizeEventProps` applies on arrival, restated here because
 * the server's copy is private to that module. A key that fails it is dropped
 * HERE rather than sent and dropped there — otherwise a customer's typo travels
 * on every event of every visitor for ever and is discarded on arrival, which
 * costs them bandwidth to achieve nothing.
 */
const PROP_KEY = /^[a-z0-9][a-z0-9_.-]*$/i;

/**
 * A custom event name: `a-z0-9._:-`, starting with a letter or a digit.
 *
 * The same rule `event-ingest.service.ts` applies on arrival, restated for the
 * same reason as {@link PROP_KEY} above: a name that fails it is dropped HERE
 * rather than sent and dropped there, so `panel('purchase completed')` fails
 * visibly on the customer's first try instead of travelling on every visitor's
 * click for ever to be discarded on arrival.
 *
 * **Both of these patterns are duplicated, and both should not be.** They are
 * storage-vocabulary rules and they belong in `@panel/shared/analytics` beside
 * the clamps this file already imports, exported rather than private to each
 * side. That move touches a file another lot is holding, so it is written down
 * here instead of made — see this lot's report.
 */
const EVENT_NAME = /^[a-z0-9][a-z0-9._:-]*$/i;

/**
 * A custom event name, or null when there is nothing to send.
 *
 * ## The reserved prefix
 *
 * `$` belongs to Panel. Its own events — `$pageview` today, whatever is next —
 * are stored in the same column as a customer's, which is what lets the
 * dashboard's "top events" list show both and lets a funnel later step across
 * the two kinds without a union query. Reserving one character is what keeps
 * them distinguishable: without it, any script on any page could call
 * `panel('$pageview')` in a loop and inflate a site's headline number beyond
 * anyone's ability to separate the real ones out, because there would be
 * nothing to separate them BY.
 *
 * Refused rather than renamed. A customer who really wanted `$promo` gets
 * silence and reads the documentation; a `promo` they did not ask for
 * appearing in their dashboard would be worse.
 *
 * **This is the browser half of a rule the ingest route enforces again.** The
 * server is the trust boundary — this path takes no credential beyond a key
 * that sits in public page source — and this copy is here so that the event
 * does not leave the page to be discarded on arrival.
 */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const name = raw.trim();
  if (!name) return null;
  // Redundant against EVENT_NAME, which cannot start with `$` either, and kept
  // because it is the rule a reader is looking for when they ask why `$promo`
  // vanished. The pattern enforces it; this line explains it.
  if (name.charAt(0) === RESERVED_EVENT_NAME_PREFIX) return null;
  if (!EVENT_NAME.test(name)) return null;
  return name.slice(0, MAX_EVENT_NAME_LENGTH);
}

/**
 * An e-mail address, in the loosest shape that still is one: something, an `@`,
 * something, a dot, something.
 *
 * Deliberately loose, because the two failure directions are not symmetrical.
 * Over-redacting mangles a property value and costs a little reporting detail
 * the customer can fix by renaming their own field. Under-redacting puts a
 * person's address in our database, which no later change takes back.
 *
 * It redacts rather than rejects: dropping the whole property would make the
 * event arrive subtly wrong with no trace, and the customer would spend the
 * afternoon debugging a missing field instead of noticing they were sending an
 * address.
 */
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

/**
 * Scalars only, clamped, capped, e-mails redacted.
 *
 * ## Why objects and arrays are refused rather than flattened
 *
 * Flattening is the obliging thing to do and it is how a property bag becomes a
 * document. `{ user: { id, email, plan, address } }` flattened is four columns
 * of somebody's customer record arriving because one developer passed the whole
 * object. Refused, it is a field that visibly does not appear — the kind of
 * failure somebody fixes in a minute.
 *
 * `null` and `undefined` are dropped: "absent" and "present and empty" are the
 * same fact for a dimension nobody can group by.
 */
export function cleanProps(raw: unknown): Record<string, EventPropValue> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, EventPropValue> = {};
  let kept = 0;
  for (const key in raw as Record<string, unknown>) {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
    if (kept >= MAX_EVENT_PROPS) break;
    if (!key || key.length > MAX_PROP_KEY_LENGTH || !PROP_KEY.test(key)) continue;
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'number') {
      // `NaN` and `Infinity` are not JSON: `JSON.stringify` turns both into
      // `null`, so a value we meant to drop would arrive as a stored hole the
      // server cannot tell from an absent field.
      if (!isFinite(value)) continue;
      out[key] = value;
    } else if (typeof value === 'string') {
      const text = value.replace(EMAIL, ':id').slice(0, MAX_PROP_VALUE_LENGTH);
      if (!text) continue;
      out[key] = text;
    } else {
      continue;
    }
    kept++;
  }
  return kept ? out : undefined;
}
